import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Field } from 'redux-form'
import { Utils } from '../../services/';

import './TranslatableField.css';

const is_focus_inside = (container, related_target) => {
    if (!container)
        return false;
    if (!related_target)
        return false;
    return container.contains(related_target);
};

const normalize_texts = (raw) => {
    if (raw && typeof raw === 'object' && !Array.isArray(raw))
        return { ...raw };
    return {};
};

const serialize_texts = (raw) => JSON.stringify(normalize_texts(raw));

const event_or_value = (event_or_value) => {
    if (event_or_value && typeof event_or_value === 'object' && 'target' in event_or_value)
        return event_or_value.target.value;
    return event_or_value ?? '';
};

/**
 * Bind the whole translatable object (`name`) instead of `name.fr` / `name.en`.
 * Nested paths like `attributes[0].name.fr` are not reliably writable with
 * redux-form when the parent is a class instance; the WYSIWYG already binds
 * the object and stays editable.
 */
const TranslatableFieldView = ({
    input,
    meta,
    placeholder,
    fieldComponent = 'input',
    optional,
    original,
    readOnly,
    onBlurExtra,
    autoFocus,
    className = 'form-control',
    type = 'text',
    rows,
    id,
    autoComplete,
    onInvalid,
    onInput,
    'data-tip': dataTip,
    'data-for': dataFor,
}) => {
    const langs_key = (original ? 'original|' : '') + Utils.langs().join('|');
    const langs = useMemo(
        () => [...new Set(langs_key.split('|').filter(Boolean))],
        [langs_key]
    );
    const [selectedLang, setSelectedLang] = useState(langs[0]);
    const [focused, setFocused] = useState(false);
    const [texts, setTexts] = useState(() => normalize_texts(input.value));
    const root_ref = useRef(null);
    const last_emitted_ref = useRef(normalize_texts(input.value));
    const on_change_ref = useRef(input.onChange);

    useEffect(() => {
        on_change_ref.current = input.onChange;
    }, [input.onChange]);

    useEffect(() => {
        if (!langs.includes(selectedLang))
            setSelectedLang(langs[0]);
    }, [langs, selectedLang]);

    useEffect(() => {
        if (focused)
            return;
        const incoming = normalize_texts(input.value);
        const incoming_s = serialize_texts(incoming);
        const emitted_s = serialize_texts(last_emitted_ref.current);
        if (incoming_s === emitted_s)
            return;
        const incoming_empty = Object.values(incoming).every((v) => !v);
        const emitted_empty = Object.values(last_emitted_ref.current || {}).every((v) => !v);
        // Keep local text if redux-form briefly emits {} while the field is dirty.
        // Honor a pristine empty value (reinitialize / new form) so create does
        // not keep the last visited title.
        if (incoming_empty && !emitted_empty && meta?.dirty)
            return;
        last_emitted_ref.current = incoming;
        setTexts(incoming);
    }, [input.value, focused, meta?.dirty]);

    const change_lang = (lang, value) => {
        const next = { ...(last_emitted_ref.current || {}), [lang]: value };
        last_emitted_ref.current = next;
        setTexts(next);
        on_change_ref.current?.(next);
    };

    const current_value = texts[selectedLang] ?? '';
    const is_required = !optional;
    const Comp = fieldComponent;
    const is_native = typeof Comp === 'string';

    const handle_blur = (event) => {
        setFocused(false);
        // Pass the translatable object. A DOM event would make redux-form
        // replace it with event.target.value (a string) → EISNOTOBJ.
        input.onBlur?.(last_emitted_ref.current);
        if (!onBlurExtra)
            return;
        if (is_focus_inside(root_ref.current, event.relatedTarget))
            return;
        onBlurExtra(event);
    };

    const field_input = {
        value: current_value,
        onChange: (value) => change_lang(selectedLang, event_or_value(value)),
        onFocus: () => {
            setFocused(true);
            input.onFocus?.();
        },
        onBlur: handle_blur,
    };

    return (
        <div className="TranslatableField" ref={root_ref}>
            <div className="lang-switchers">
                {langs.length > 1 && langs.map((l) => (
                    <button type="button" key={l} onClick={() => setSelectedLang(l)} className={"lang-switcher" + (selectedLang === l ? " active" : "")}>
                        {l.toUpperCase()}
                    </button>
                ))}
            </div>
            <div className="translatable-field-wrapper">
                {is_native ? (
                    <Comp
                        data-tip={dataTip}
                        data-for={dataFor}
                        required={is_required}
                        className={className}
                        type={type}
                        rows={rows}
                        id={id || `${input.name}.${selectedLang}`}
                        autoComplete={autoComplete || `${input.name}.${selectedLang}`}
                        placeholder={placeholder}
                        readOnly={readOnly}
                        disabled={readOnly}
                        autoFocus={autoFocus}
                        onInvalid={onInvalid}
                        onInput={onInput}
                        {...field_input}
                        name={undefined}
                    />
                ) : (
                    <Comp
                        data-tip={dataTip}
                        data-for={dataFor}
                        required={is_required}
                        className={className}
                        readOnly={readOnly}
                        input={field_input}
                    />
                )}
            </div>
        </div>
    );
};

const TranslatableField = ({
    name,
    placeholder,
    component = 'input',
    optional,
    original,
    readOnly,
    onBlur,
    autoFocus,
    className,
    type,
    ...props
}) => (
    <Field
        name={name}
        component={TranslatableFieldView}
        placeholder={placeholder}
        fieldComponent={component}
        optional={optional}
        original={!!original}
        readOnly={readOnly || props.disabled}
        onBlurExtra={onBlur}
        autoFocus={autoFocus}
        className={className}
        type={type}
        rows={props.rows}
        id={props.id}
        autoComplete={props.autoComplete}
        onInvalid={props.onInvalid}
        onInput={props.onInput}
        data-tip={props['data-tip']}
        data-for={props['data-for']}
    />
);

export { TranslatableFieldView };
export default TranslatableField;
